import { useMutation } from "@apollo/client";
import NoteAdd from "@mui/icons-material/NoteAdd";
import { LoadingButton } from "@mui/lab";
import { Alert, Button, Link, Stack } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { gql } from "generated-graphql";
import { Link as RouterLink } from "react-router-dom";

export const CREATE_CERS_REVISION = gql(`
  mutation CreateCersRevision($findingId: ID!) {
    createCersRevision(findingId: $findingId) {
      alreadyOpen
      report {
        id
      }
    }
  }
`);

type Props = {
  findingId: string;
  isFacilityMatched: boolean;
  /** CERS-side facility id — scopes the matcher deep link to exactly this row. */
  cersFacilityId: string;
};

// Deep link into the staff facility matcher, scoped by exact CERS id via
// omnisearch free text (its search covers name / EPA id / address / cersId).
const matcherPath = (cersFacilityId: string) =>
  `/staff/cers/facilities?omnisearch=${encodeURIComponent(cersFacilityId)}`;

export function CreateRevisionAction({ findingId, isFacilityMatched, cersFacilityId }: Props) {
  const alerts = useAlerts();

  // awaitRefetchQueries: the button's loading state should hold until the
  // drawer + grid reflect the resolved finding, not just the mutation.
  const [createRevision, { loading, error }] = useMutation(CREATE_CERS_REVISION, {
    variables: { findingId },
    onError: () => undefined,
    refetchQueries: ["StaffCersRejectionFindings", "CersFindingDetail"],
    awaitRefetchQueries: true,
  });

  if (!isFacilityMatched) {
    return (
      <Stack spacing={1}>
        <Alert severity="warning">
          This CERS facility isn&apos;t matched to an Encamp facility yet — match it first.{" "}
          <Link component={RouterLink} to={matcherPath(cersFacilityId)}>
            Match facility
          </Link>
        </Alert>
        <Button variant="contained" disabled>
          Create revision
        </Button>
      </Stack>
    );
  }

  // Creation is a HAND-OFF, not a start-working-now: stay in the tracker so
  // the CSM keeps triaging the queue. The awaited refetch flips the drawer to
  // Resolved with a "View revision" link for the times they DO want to go;
  // the toast carries the created-vs-linked distinction.
  const handleCreate = async () => {
    const { data } = await createRevision();
    const result = data?.createCersRevision;
    if (!result) return; // mutation error — surfaced by the alert below
    alerts.success(
      result.alreadyOpen ? "Linked to the existing open revision." : "Revision report created."
    );
  };

  return (
    <Stack spacing={1}>
      {error && <Alert severity="error">{error.message}</Alert>}
      {/* Creating the revision report takes a few seconds server-side; the
          start-spinner + label swap keep it from looking like a dead button. */}
      <LoadingButton
        variant="contained"
        loading={loading}
        loadingPosition="start"
        startIcon={<NoteAdd />}
        onClick={() => void handleCreate()}
      >
        {loading ? "Creating revision…" : "Create revision"}
      </LoadingButton>
    </Stack>
  );
}
